import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import { ManagementSupportRequestDtoIn } from '@/models/in/ManagementSupportRequestDtoIn';
import { SupplierTaxApprovalStatus } from '@/models/in/SupplierFullDtoIn';
import {
  computeSupplierApprovalGate,
  isApprovalStatus,
  SupplierApprovalIssue,
  SupplierWithholdingRow,
} from '@/helpers/supplierApprovalGate';

interface SupplierApprovalGateProps {
  /** True when the parent wants to start the gate flow (user clicked "save"). */
  open: boolean;
  /** Requests the parent is about to update. */
  selectedRequests: ManagementSupportRequestDtoIn[];
  /** Status the parent wants to transition them to (e.g. "PendingExecution"). */
  newStatus: string;
  /** Called once the user has acknowledged every gate step and we should proceed with the actual update. */
  onContinue: () => void;
  /** Called when the user backs out of any gate dialog. The parent should keep its own dialog open. */
  onCancel: () => void;
}

type Stage = 'idle' | 'issues' | 'withholdings' | 'done';

/**
 * Pre-flight check before the secretariat approves a payment for a supplier:
 *
 *   1. ApprovalIssuesDialog (first, when relevant): if any selected supplier
 *      is in an actionable approval state (PendingValidUntil / PendingDocument
 *      / Expired) we show the secretariat a confirmation dialog listing them
 *      so they can choose to proceed anyway or back out. We don't block - the
 *      secretariat is the compliance authority - but we make sure the choice
 *      is explicit.
 *
 *   2. WithholdingsNoticeDialog (second, when relevant): an informational
 *      reminder listing every supplier with an active withholding percentage
 *      (> 0) so the secretariat knows to deduct manually before paying. Shows
 *      only supplier name + percentage (no amounts / request counts).
 *
 *   3. Once both dialogs are dismissed (or skipped because there's nothing
 *      to show), the gate fires onContinue and the parent calls its API.
 *
 * When the new status isn't an approval status, both dialogs are skipped and
 * onContinue is called immediately.
 */
const SupplierApprovalGate = ({
  open,
  selectedRequests,
  newStatus,
  onContinue,
  onCancel,
}: SupplierApprovalGateProps) => {
  const { approvalIssues, withholdings } = useMemo(
    () => computeSupplierApprovalGate(selectedRequests),
    [selectedRequests],
  );

  const requiresApprovalGate = isApprovalStatus(newStatus);
  const hasIssues = requiresApprovalGate && approvalIssues.length > 0;
  const hasWithholdings = requiresApprovalGate && withholdings.length > 0;

  // We fire onContinue through an effect (instead of inside the click
  // handler) so the user can see the last dialog disappear before the actual
  // update is sent.
  const [stage, setStage] = useState<Stage>('idle');
  // Guard against the second StrictMode pass (and unstable onContinue refs) firing the
  // continuation twice - the parent's mutation would otherwise run twice for a single
  // user gesture.
  const continuedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setStage('idle');
      continuedRef.current = false;
      return;
    }
    if (hasIssues) setStage('issues');
    else if (hasWithholdings) setStage('withholdings');
    else setStage('done');
  }, [open, hasIssues, hasWithholdings]);

  useEffect(() => {
    if (stage === 'done' && !continuedRef.current) {
      continuedRef.current = true;
      onContinue();
    }
  }, [stage, onContinue]);

  if (!open || stage === 'idle' || stage === 'done') return null;

  if (stage === 'issues') {
    return (
      <ApprovalIssuesDialog
        issues={approvalIssues}
        onCancel={onCancel}
        onContinue={() => setStage(hasWithholdings ? 'withholdings' : 'done')}
      />
    );
  }

  return (
    <WithholdingsNoticeDialog
      withholdings={withholdings}
      onCancel={onCancel}
      onContinue={() => setStage('done')}
    />
  );
};

const ApprovalIssuesDialog = ({
  issues,
  onCancel,
  onContinue,
}: {
  issues: SupplierApprovalIssue[];
  onCancel: () => void;
  onContinue: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <MainDialogLayout
      open
      onOpenChange={(isOpen: boolean) => { if (!isOpen) onCancel(); }}
      title={t('supplierApprovalGate.issuesTitle')}
    >
      <div className="flex flex-col gap-4 p-6 min-w-[520px] max-w-[680px]">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-amber-800 body-sm-semibold">
            {t('supplierApprovalGate.issuesIntro')}
          </p>
        </div>
        <ul className="flex flex-col gap-2">
          {issues.map((issue) => (
            <li
              key={issue.supplierId}
              className="flex items-center justify-between gap-3 border border-(--border-input-default) rounded-md p-3"
            >
              <span className="body-md-semibold text-(--font-heading)">{issue.supplierName}</span>
              <span className="body-sm-semibold text-amber-700">
                {describeIssueStatus(issue, t as unknown as (k: string, p?: object) => string)}
              </span>
            </li>
          ))}
        </ul>
        <p className="body-sm-regular text-(--font-body-2)">
          {t('supplierApprovalGate.issuesPrompt')}
        </p>
        <div className="flex gap-3 justify-end pt-2">
          <Button variant="white" onClick={onCancel}>{t('cancel')}</Button>
          <Button variant="primary" onClick={onContinue}>
            {t('supplierApprovalGate.continueAnyway')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

const WithholdingsNoticeDialog = ({
  withholdings,
  onCancel,
  onContinue,
}: {
  withholdings: SupplierWithholdingRow[];
  onCancel: () => void;
  onContinue: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <MainDialogLayout
      open
      onOpenChange={(isOpen: boolean) => { if (!isOpen) onCancel(); }}
      title={t('supplierApprovalGate.withholdingsTitle')}
    >
      <div className="flex flex-col gap-4 p-6 min-w-[480px] max-w-[640px]">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-blue-800 body-sm-semibold">
            {t('supplierApprovalGate.withholdingsIntro')}
          </p>
        </div>
        <ul className="flex flex-col gap-2">
          {withholdings.map((row) => (
            <li
              key={row.supplierId}
              className="flex items-center justify-between gap-3 border border-(--border-input-default) rounded-md p-3"
            >
              <span className="body-md-semibold text-(--font-heading)">{row.supplierName}</span>
              <span className="body-md-semibold text-(--font-heading)">{row.percentage}%</span>
            </li>
          ))}
        </ul>
        <div className="flex gap-3 justify-end pt-2">
          <Button variant="white" onClick={onCancel}>{t('cancel')}</Button>
          <Button variant="primary" onClick={onContinue}>
            {t('supplierApprovalGate.acknowledge')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

function describeIssueStatus(
  issue: SupplierApprovalIssue,
  t: (k: string, p?: object) => string,
): string {
  switch (issue.status) {
    case SupplierTaxApprovalStatus.PendingValidUntil:
      return t('suppliersManagement.taxApprovalStatus.PendingValidUntil');
    case SupplierTaxApprovalStatus.PendingDocument:
      return t('suppliersManagement.taxApprovalStatus.PendingDocument');
    case SupplierTaxApprovalStatus.Expired:
      return issue.taxApprovalValidUntil
        ? t('supplierApprovalGate.expiredOnDate', { date: new Date(issue.taxApprovalValidUntil).toLocaleDateString('he-IL') })
        : t('suppliersManagement.taxApprovalStatus.Expired');
    default:
      return '';
  }
}

export default SupplierApprovalGate;
